import { useNavigate, useParams } from "react-router-dom";
import setThemeColor from "../../utils/setThemeColor/setThemeColor";
import { NavMenu } from "../../components/feature/NavMenu/NavMenu";
import DeletePopup from "../../components/feature/DeletePopup/DeletePopup";
import { ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { api, Api } from "../../services/Api";
import { IOptions } from "../../components/feature/NavMenu/elements/AdditionalOptionsMenu/AdditionalOptionsMenu";
import { setIsWaiting, setToast } from "../../redux/actions/appActions";
import { useTranslation } from "react-i18next";
import ShowRecipe, { IShowRecipe } from "../../components/feature/ShowRecipe/ShowRecipe";
import setWebPageTitle from "../../utils/setWebPageTitle/setWebPageTitle";
import setWebPagePreviewImage from "../../utils/setWebPagePreviewImage/setWebPagePreviewImage";

const ShowRecipePage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useSelector((state: RootState) => state.client.isMobile);
  const [error, setError] = useState(null);
  const [navigationTitle, setNavigationTitle] = useState<ReactElement<any, any>>(<span></span>);
  const isLoggedIn = useSelector((state: RootState) => state.client.isLoggedIn);
  const [username, setUsername] = useState<string>("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [recipeUsername, setRecipeUsername] = useState("");
  const [isSplitViewDisabled, setIsSplitViewDisabled] = useState(isMobile);
  const [recipeData, setRecipeData] = useState<IShowRecipe | null>(null);
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [hideUserRecipesButton, setHideUserRecipesButton] = useState<boolean>(false);
  const [splitViewButtonText, setSplitViewButtonText] = useState<string>(t("containers.showRecipePage.splitView"));
  const [splitViewButtonIcon, setSplitViewButtonIcon] = useState<ReactElement<any, any>>(
    <i className="bi bi-layout-sidebar-inset-reverse"></i>,
  );
  const [url] = useState("getrecipe/" + useParams().id);
  const [id] = useState(useParams().id);
  const [options, setOptions] = useState<IOptions[]>([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const triggerAction = (action: string) => {
    if (action === "DELETE") {
      setIsDeletePopupVisible(true);
    } else if (action === "EDIT") {
      navigate("/recipe/edit/" + id);
    } else if (action === "USERNAME") {
      navigate(`/user/${recipeUsername}`);
    } else if (action === "SPLIT_VIEW" && isMobile) {
      if (isSplitViewDisabled) {
        setSplitViewButtonText(t("containers.showRecipePage.singleView"));
        setSplitViewButtonIcon(<i className="bi bi-square"></i>);
      } else {
        setSplitViewButtonText(t("containers.showRecipePage.splitView"));
        setSplitViewButtonIcon(<i className="bi bi-layout-sidebar-inset-reverse"></i>);
      }
      setIsSplitViewDisabled(!isSplitViewDisabled);
    } else if (action === "PIN") {
      pinRecipe();
    } else {
      dispatch(
        setToast({
          text: 'The action "' + action + '" is not recognizable',
          isPositive: false,
        }),
      );
    }
  };

  const shouldBeDeleted = (shouldBeDeleted: boolean) => {
    if (shouldBeDeleted) {
      const bodyJson = {
        RecipeId: id,
        UserId: recipeData?.userId,
      };
      Api.put("deleterecipe", bodyJson).then((response) => {
        if (response.status === 200) {
          dispatch(
            setToast({
              text: "Recipe successfully deleted",
              isPositive: true,
            }),
          );
        } else {
          dispatch(
            setToast({
              text: "Recipe not deleted",
              isPositive: false,
            }),
          );
        }
      });
      navigate("/");
    }
    setIsDeletePopupVisible(false);
  };

  const pinRecipe = () => {
    var bodyJson = {
      Id: recipeData?.id,
    };
    Api.post("pinrecipe", bodyJson).then((response) => {
      if (response.status === 200) {
        dispatch(
          setToast({
            text: "Recipe successfully pinned",
            isPositive: true,
          }),
        );
      } else {
        dispatch(setToast({ text: "Recipe not pinned", isPositive: false }));
      }
    });
  };

  useEffect(() => {
    if (isMobile && isLoggedIn) {
      setOptions([
        {
          action: "SPLIT_VIEW",
          name: splitViewButtonText,
          icon: splitViewButtonIcon,
        },
        {
          link: "/",
          name: t("containers.myRecipesPage.title"),
          icon: <i className="bi bi-collection"></i>,
        },
        {
          link: "/explore",
          name: t("containers.explorePage.title"),
          icon: <i className="bi bi-globe-americas"></i>,
        },
        {
          link: "/info",
          name: t("containers.infoPage.title"),
          icon: <i className="bi bi-info-circle"></i>,
        },
      ]);
    } else if (isMobile && !isLoggedIn) {
      setOptions([
        {
          action: "SPLIT_VIEW",
          name: splitViewButtonText,
          icon: splitViewButtonIcon,
        },
        {
          link: "/explore",
          name: t("containers.explorePage.title"),
          icon: <i className="bi bi-globe-americas"></i>,
        },
        {
          link: "/info",
          name: t("containers.infoPage.title"),
          icon: <i className="bi bi-info-circle"></i>,
        },
      ]);
    } else if (!isMobile && isLoggedIn) {
      setOptions([
        {
          link: "/",
          name: t("containers.myRecipesPage.title"),
          icon: <i className="bi bi-collection"></i>,
        },
        {
          link: "/explore",
          name: t("containers.explorePage.title"),
          icon: <i className="bi bi-globe-americas"></i>,
        },
        {
          link: "/info",
          name: t("containers.infoPage.title"),
          icon: <i className="bi bi-info-circle"></i>,
        },
      ]);
    } else if (!isMobile && !isLoggedIn) {
      setOptions([
        {
          link: "/explore",
          name: t("containers.explorePage.title"),
          icon: <i className="bi bi-globe-americas"></i>,
        },
        {
          link: "/info",
          name: t("containers.infoPage.title"),
          icon: <i className="bi bi-info-circle"></i>,
        },
      ]);
    }
  }, [isMobile, splitViewButtonText, isLoggedIn]);

  useEffect(() => {
    setIsSplitViewDisabled(isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (isLoggedIn) {
      Api.get("usernamefromaccesstoken")
        .then((response) => {
          return response.data;
        })
        .then((result) => {
          setUsername(result.message);
        });
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (username === recipeUsername) {
      setNavigationTitle(
        <span>
          <i className="bi bi-collection"></i> My Recipes
        </span>,
      );
      setHideUserRecipesButton(true);
    } else {
      setNavigationTitle(<span>@{recipeUsername}</span>);
    }
  }, [username, recipeData, recipeUsername]);

  useEffect(() => {
    setWebPageTitle(recipeData?.name);
    setWebPagePreviewImage(recipeData?.imageUrl);
    if (recipeData?.userId) {
      Api.get(`getrecipe/username/${recipeData?.userId}`)
        .then((res) => res.data)
        .then((result) => {
          setRecipeUsername(result.data);
        });
    }
  }, [recipeData]);

  useEffect(() => {
    setThemeColor("#ffffff");
  }, []);

  useEffect(() => {
    api
      .get(`api/${url}`)
      .then((res) => res.data)
      .then(
        (result) => {
          setIsLoaded(true);
          setRecipeData(result);
          dispatch(setIsWaiting(false));
        },
        (error) => {
          setIsLoaded(true);
          setError(error);
          dispatch(setIsWaiting(false));
        },
      );
  }, [url]);

  if (error) {
    console.log("ERROR: cannot download recipe data!");
    return <div />;
  } else if (!isLoaded) {
    return <div />;
  } else if (recipeData) {
    return (
      <div>
        <NavMenu options={options} triggerAction={triggerAction} title={navigationTitle} />
        <ShowRecipe
          id={recipeData.id}
          description={recipeData.description}
          ingredients={recipeData.ingredients}
          imageUrl={recipeData.imageUrl}
          sourceUrl={recipeData.sourceUrl}
          name={recipeData.name}
          isSplitViewDisabled={isSplitViewDisabled}
          isPublic={recipeData.isPublic}
          username={recipeUsername}
          userId={recipeData.userId}
          preparationTime={recipeData.preparationTime}
          hideUserRecipesButton={hideUserRecipesButton}
          portionCount={recipeData.portionCount}
          triggerAction={triggerAction}
        />
        {isDeletePopupVisible ? <DeletePopup shouldBeDeleted={shouldBeDeleted} userId={recipeData?.userId} /> : <div />}
      </div>
    );
  } else {
    console.log("ERROR: recipeData include NULL value!");
    return <div />;
  }
};

export default ShowRecipePage;
